import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, ShoppingBag, Trash2, Minus, Plus, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";

export default function Cart({ auth }) {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasPhysical, setHasPhysical] = useState(false);

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    const savedCart = localStorage.getItem('softRitualCart');
    if (savedCart) {
      const cartItems = JSON.parse(savedCart);
      setCart(cartItems);
      
      // Check if any items are physical products
      try {
        for (const item of cartItems) {
          const response = await fetch(`${API}/store/products/${item.product_id}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const product = await response.json();
            if (product.product_type === "physical") {
              setHasPhysical(true);
              break;
            }
          }
        }
      } catch (e) {
        console.error("Error checking product types:", e);
      }
    }
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    
    const newCart = cart.map(item =>
      item.product_id === productId
        ? { ...item, quantity: newQuantity }
        : item
    );
    setCart(newCart);
    localStorage.setItem('softRitualCart', JSON.stringify(newCart));
  };

  const removeItem = (productId) => {
    const newCart = cart.filter(item => item.product_id !== productId);
    setCart(newCart);
    localStorage.setItem('softRitualCart', JSON.stringify(newCart));
    toast.success("Item removed from cart");
    
    // Check if we still have physical products
    if (newCart.length === 0) {
      setHasPhysical(false);
    }
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const shipping = hasPhysical ? 7.00 : 0;
  const total = subtotal + shipping;

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/store/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          cart_items: cart.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity
          })),
          origin_url: window.location.origin
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Checkout failed");
      }
      
      // Clear cart before redirecting
      localStorage.removeItem('softRitualCart');
      
      // Redirect to Stripe checkout (Stripe will collect shipping address)
      window.location.href = data.checkout_url;
      
    } catch (error) {
      const message = error.message || "Something went wrong. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
        <header className="border-b border-[#E6E2DD]">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/store" className="flex items-center gap-2 text-[#6E6A66] hover:text-[#2D2A26]">
              <ArrowLeft className="w-5 h-5" />
              <span>Continue Shopping</span>
            </Link>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <ShoppingBag className="w-16 h-16 text-[#E6E2DD] mx-auto mb-4" />
            <h2 
              className="text-2xl font-light text-[#2D2A26] mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Your cart is empty
            </h2>
            <p className="text-[#A8A4A0] mb-6">
              Explore our sensory supports to find something special.
            </p>
            <Link to="/store">
              <Button className="rounded-full px-8 bg-[#A87C6D] hover:bg-[#956d5f] text-white">
                Browse Products
              </Button>
            </Link>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      {/* Header */}
      <header className="border-b border-[#E6E2DD]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/store" className="flex items-center gap-2 text-[#6E6A66] hover:text-[#2D2A26]">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Continue Shopping</span>
          </Link>
          
          <h1 
            className="text-xl font-light text-[#2D2A26]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Your Cart
          </h1>
          
          <div className="w-20"></div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-6 py-8 w-full">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-3 space-y-4">
            {cart.map(item => (
              <div 
                key={item.product_id}
                className="flex gap-4 p-4 bg-white rounded-xl border border-[#E6E2DD]"
              >
                {/* Image */}
                <div className="w-20 h-20 bg-[#F5F2ED] rounded-lg overflow-hidden flex-shrink-0">
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#A8A4A0]">
                      <ShoppingBag className="w-6 h-6" />
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <Link to={`/store/${item.product_id}`}>
                    <h3 className="font-medium text-[#2D2A26] hover:text-[#A87C6D] truncate">
                      {item.name}
                    </h3>
                  </Link>
                  <p className="text-[#A87C6D] mt-1">${item.price.toFixed(2)}</p>
                  
                  {/* Quantity */}
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                      className="w-6 h-6 rounded-full border border-[#E6E2DD] flex items-center justify-center text-[#6E6A66] hover:bg-[#F5F2ED]"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-6 text-center text-sm">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                      className="w-6 h-6 rounded-full border border-[#E6E2DD] flex items-center justify-center text-[#6E6A66] hover:bg-[#F5F2ED]"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Remove & Total */}
                <div className="flex flex-col items-end justify-between">
                  <button
                    onClick={() => removeItem(item.product_id)}
                    className="text-[#A8A4A0] hover:text-[#C8553D]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <span className="font-medium text-[#2D2A26]">
                    ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-[#E6E2DD] p-6 sticky top-24">
              <h2 
                className="text-xl font-light text-[#2D2A26] mb-6"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Order Summary
              </h2>

              {/* Totals */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-[#6E6A66]">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                {hasPhysical && (
                  <div className="flex justify-between text-[#6E6A66]">
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      <span>Shipping</span>
                    </div>
                    <span>${shipping.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-medium text-[#2D2A26] pt-3 border-t border-[#E6E2DD]">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              {hasPhysical && (
                <p className="text-xs text-[#A8A4A0] mb-4">
                  Shipping address will be collected at checkout.
                </p>
              )}

              <Button
                onClick={handleCheckout}
                disabled={loading}
                className="w-full rounded-full py-6 bg-[#A87C6D] hover:bg-[#956d5f] text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  "Checkout"
                )}
              </Button>

              <p className="text-xs text-[#A8A4A0] text-center mt-4">
                Secure checkout powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
